import React, { useEffect, useState } from 'react';
import { Anchor, Button, TextArea, Upload } from '@keplerco/core';
import styles from '../freemium.onboarding.module.css';
import classNames from 'classnames';
import layoutStyles from '../../../../design/layout.module.css';
import { useAppActions, useAppState } from '../../../../overmind';
import { FreemiumSupportingFileDto } from '../../../../overmind/actions';
import { PagePath } from '../../../../navigation/navigation.enums';
import { FetchType } from '../../../../enums';
import { EntitiesCardWidget } from '../../../../components/cards/entities-card.widget';

// Props for the description page
interface DescriptionPageProps {
  onNext: () => void;
  onBack: () => void;
}

/**
 * Description onboarding step
 * Collects company description and document uploads
 */
export function DescriptionPage({ onNext, onBack }: DescriptionPageProps): JSX.Element {
  const actions = useAppActions();
  const { freemiumAccount } = useAppState();

  // State for form fields
  const [companyDescription, setCompanyDescription] = useState<string>(freemiumAccount?.companyDescription || '');
  const [uploadedFiles, setUploadedFiles] = useState<FreemiumSupportingFileDto[]>(freemiumAccount?.supportingFiles || []);
  const [isUploading, setIsUploading] = useState(false);

  // Form validation
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [uploadError, setUploadError] = useState<string | null>(null);


  useEffect(() => {
    if (!freemiumAccount) return;
    setUploadedFiles(freemiumAccount.supportingFiles);
  }, [freemiumAccount?.supportingFiles]);
  // Handle file upload
  const handleFileUpload = async (files: File[]) => {
    if (files && files.length > 0) {
      setIsUploading(true);
      setUploadError(null);

      try {
        for (const file of files) {
          await actions.uploadCompanyInformationFile({
            file: file
          });
        }
        await actions.getFreemiumAccount();
        setIsUploading(false);
      } catch (error) {
        setUploadError('Failed to upload files. Please try again.');
        setIsUploading(false);
      }
    }
  };

  // Handle removing an uploaded file
  const handleRemoveFile = (fileName: string) => {
    setUploadedFiles(uploadedFiles.filter(file => file.fileName !== fileName));
  };

  // Handle form submission
  const handleContinue = async () => {
    // Validate form
    const newErrors: { [key: string]: string } = {};

    if (!companyDescription) {
      newErrors.description = 'Please provide a description of your company';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    // Save data to state
    actions.startLoader({ path: PagePath.onboarding, type: FetchType.Sending });

    await actions.updateCompanyDescription({
      companyDescription: companyDescription,
      supportingFiles: uploadedFiles
    });

    await actions.getFreemiumAccount();
    actions.stopLoader(PagePath.onboarding);
    // Go to next step
    onNext();
  };

  const handleBack = () => {
    onBack();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, width: '100%' }}>
      <EntitiesCardWidget title="Company Description" description="Tell us more about your company to help us understand your business better.">
        <TextArea
          value={companyDescription}
          onChange={(e) => setCompanyDescription(e.target.value)}
          placeholder="Describe your company, its mission, and the key areas where skills and roles are important..."
          rows={10}
          responsive
          label="Company Description"
          validation={{
            dirty: !!errors.description,
            invalid: !!errors.description,
            message: errors.description
          }}
        />
      </EntitiesCardWidget>

      <EntitiesCardWidget title="Upload Documents" description="Upload relevant documents such as organization charts, job descriptions, or other materials that would help us understand your company's structure and needs.">
        {isUploading ? (
          <div className={styles.uploadingIndicator}>
            <div style={{ padding: '24px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.1rem', marginBottom: '8px' }}>Uploading your files...</div>
              <div style={{ width: '100%', height: '4px', backgroundColor: '#f0f0f0', borderRadius: '2px', overflow: 'hidden' }}>
                <div
                  className={styles.progressBar}
                  style={{
                    height: '100%',
                    backgroundColor: 'var(--primary)',
                    width: '60%',
                    borderRadius: '2px',
                    animation: 'progress 1.5s infinite linear'
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          <Upload
            description="Drop your files here or <strong>Browse</strong>"
            accept={[
              // Original types
              'text/csv',
              'application/vnd.ms-excel',
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',

              // Text files
              'text/plain', // .txt

              // Structured data files
              'application/json', // .json
              'application/xml', 'text/xml', // .xml

              // Document files
              'application/pdf', // .pdf
              'application/msword', // .doc
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx

              // Web files
              'text/html', // .html, .htm

              // Code files
              'text/x-csharp', // .cs
              'text/javascript', // .js
              'text/x-python', // .py
              'text/x-java', // .java
              'text/x-c++src', // .cpp
              'text/x-csrc', // .c
              'application/x-httpd-php', // .php
              'text/x-ruby', // .rb
              'text/x-go', // .go
              'application/typescript', // .ts
              'text/x-swift', // .swift

              // Markdown and other text-based formats
              'text/markdown', // .md, .markdown
              'application/x-yaml', 'text/yaml', // .yaml, .yml
              'application/toml', // .toml
            ] as any}
            error={uploadError || undefined}
            onChange={handleFileUpload}
            multiple
          />
        )}

        {uploadedFiles.length > 0 && (
          <div className={styles.uploadedFilesList}>
            <h4>Uploaded Files ({uploadedFiles.length})</h4>
            <ul className={styles.filesList}>
              {uploadedFiles.map((file, index) => (
                <li key={index} className={styles.fileItem}>
                  <span className={styles.fileName}>{file.fileName}</span>
                  <button
                    type="button"
                    className={styles.removeButton}
                    onClick={() => handleRemoveFile(file.fileName)}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </EntitiesCardWidget>

      <footer className={classNames('card', layoutStyles.wizardFooter)}>
        <Anchor className={styles.anchor} onClick={handleBack}>
          Back
        </Anchor>
        <Button type="button" filled onClick={handleContinue}>
          Next
        </Button>
      </footer>
    </div>
  );
}
